import { SupplierInventoryDeltaResponseDto } from "../types";
import {
  applyAdjustment,
  PartConfirmationBucketEntry,
  SelectedSupplier,
  SupplierPart,
} from "./partsCalculationUtils";

/**
 * Build the composite key used to look up a part-confirmation quantity
 * for a specific supplier + root part family.
 */
export const getSupplierPartConfirmationKey = (
  supplierId: string,
  rootPartNumber: string,
): string => `${supplierId}::${rootPartNumber}`;

/**
 * Apply part-confirmation quantities to supplier parts.
 *
 * This is the LAST quantity adjustment in the pipeline:
 *   Typesense parts → offline merge → delta → THIS → metrics
 *
 * For each part, the adjusted quantity is:
 *   Math.max(0, familyQuantity - confirmationQuantity)
 *
 * Parts without a matching confirmation entry are returned unchanged.
 */
export const applySupplierPartConfirmationToParts = <T extends SupplierPart>({
  supplierId,
  parts,
  supplierPartConfirmationQuantityMap,
}: {
  supplierId: string;
  parts: T[];
  supplierPartConfirmationQuantityMap?: Map<string, number>;
}): T[] => {
  if (!supplierPartConfirmationQuantityMap?.size || !supplierId) return parts;

  return parts.map((part) => {
    const rootPartNumber = part.rootPartNumber;
    if (!rootPartNumber) return part;

    const supplierPartConfirmationQuantity =
      supplierPartConfirmationQuantityMap.get(
        getSupplierPartConfirmationKey(supplierId, rootPartNumber),
      ) ?? 0;

    if (supplierPartConfirmationQuantity <= 0) return part;

    const familyQuantity = Number(part.familyQuantity) || 0;
    const adjustedFamilyQuantity = Math.max(
      0,
      familyQuantity - supplierPartConfirmationQuantity,
    );

    return applyAdjustment(
      part,
      { label: "Part Confirmation", value: supplierPartConfirmationQuantity },
      adjustedFamilyQuantity,
    );
  });
};

/**
 * Build a lookup map from the deltas API response.
 *
 * Key:   "supplierId::rootPartNumber"
 * Value: confirmedQuantity + pendingConfirmationQuantity
 *
 * Returns an empty map if response is undefined or has no results.
 */
export const buildSupplierPartConfirmationQuantityMap = (
  response: SupplierInventoryDeltaResponseDto | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  if (!response?.results) return map;

  for (const delta of response.results) {
    if (!delta.supplierId || !delta.rootPartNumber) continue;
    const confirmationQuantity =
      (Number(delta.confirmedQuantity) || 0) +
      (Number(delta.pendingConfirmationQuantity) || 0);
    if (confirmationQuantity <= 0) continue;
    map.set(
      getSupplierPartConfirmationKey(delta.supplierId, delta.rootPartNumber),
      confirmationQuantity,
    );
  }
  return map;
};

const reserveQtyOnMap = (
  map: Map<string, number>,
  supplierId: string,
  rootPartNumber: string,
  quantity: number,
): void => {
  const root = String(rootPartNumber ?? "").trim();
  const id = String(supplierId ?? "").trim();
  const qty = Math.max(0, Number(quantity) || 0);
  if (!id || !root || qty <= 0) return;
  const key = getSupplierPartConfirmationKey(id, root);
  map.set(key, Math.max(map.get(key) ?? 0, qty));
};

/**
 * Reduce discoverable stock by qty already staged in Confirmed + PC buckets
 * so a supplier with 10 stock and 8 in a bucket shows as 2, not 10.
 */
export const overlayDiscoveryBucketReservations = (
  baseMap: Map<string, number>,
  confirmedSuppliers: SelectedSupplier[],
  partConfirmationBucket: PartConfirmationBucketEntry[],
): Map<string, number> => {
  const map = new Map(baseMap);

  for (const supplier of confirmedSuppliers) {
    for (const part of supplier.selectedParts ?? []) {
      reserveQtyOnMap(
        map,
        supplier.supplierId,
        part.rootPartNumber,
        part.familyQuantity,
      );
    }
  }

  for (const entry of partConfirmationBucket) {
    for (const part of entry.confirmationParts ?? []) {
      reserveQtyOnMap(
        map,
        entry.supplierId,
        part.rootPartNumber,
        part.askedQty,
      );
    }
  }

  return map;
};

/**
 * Declined PC responses must not contribute stock to discovery metrics.
 * Reserves all availability for those roots (available → 0 after apply).
 * Does not alter how Confirmed/PC bucket reservations are calculated.
 */
export const overlayDeclinedPartConfirmationsOnQuantityMap = (
  baseMap: Map<string, number>,
  responsesBySupplierId: Map<
    string,
    Array<{
      partId?: string;
      rootPartNumber?: string;
      status?: string;
      responseStatus?: string;
    }>
  >,
  originalParts: Array<{ partId?: string; rootPartNumber: string }> = [],
): Map<string, number> => {
  const rootByPartId = new Map(
    originalParts
      .filter((part) => part.partId)
      .map((part) => [String(part.partId), part.rootPartNumber]),
  );
  const map = new Map(baseMap);

  responsesBySupplierId.forEach((parts, supplierId) => {
    for (const part of parts ?? []) {
      const status = String(part.responseStatus ?? part.status ?? "")
        .trim()
        .toLowerCase();
      if (status !== "declined") continue;
      const root =
        String(part.rootPartNumber ?? "").trim() ||
        rootByPartId.get(String(part.partId ?? "")) ||
        "";
      if (!root || !supplierId) continue;
      map.set(
        getSupplierPartConfirmationKey(String(supplierId), root),
        Number.MAX_SAFE_INTEGER,
      );
    }
  });

  return map;
};
